export type UiThemeMode = 'light' | 'dark' | 'system'
export type UiLanguage = 'ru' | 'en'

export interface UiPrefs {
  themeMode?: UiThemeMode
  language?: UiLanguage
}

const UI_PREFS_COOKIE = 'medcifra-ui-prefs'
const USER_SETTINGS_COOKIE = 'userSettings'
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function getCookie(name: string): string | null {
  if (!isBrowser()) return null
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function getCookieDomain(): string | null {
  if (!isBrowser()) return null
  const host = window.location.hostname.toLowerCase()
  if (host === 'medcifra.ru' || host.endsWith('.medcifra.ru')) {
    return '.medcifra.ru'
  }
  return null
}

export function writeDomainCookie(
  name: string,
  value: string,
  maxAgeSeconds: number = ONE_YEAR_SECONDS,
): void {
  if (!isBrowser()) return
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${maxAgeSeconds}`,
    'SameSite=Lax',
  ]
  const domain = getCookieDomain()
  if (domain) {
    parts.push(`Domain=${domain}`)
  }
  if (window.location.protocol === 'https:') {
    parts.push('Secure')
  }
  document.cookie = parts.join('; ')
}

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

export function readUiPrefs(): UiPrefs {
  return safeParse<UiPrefs>(getCookie(UI_PREFS_COOKIE)) ?? {}
}

export function writeUiPrefs(patch: UiPrefs): UiPrefs {
  const next = { ...readUiPrefs(), ...patch }
  writeDomainCookie(UI_PREFS_COOKIE, JSON.stringify(next))
  if (next.language) {
    writeDomainCookie(USER_SETTINGS_COOKIE, JSON.stringify({ language: next.language }))
  }
  return next
}

export function readUiLanguage(): UiLanguage {
  const prefs = readUiPrefs()
  if (prefs.language) return prefs.language
  const legacy = safeParse<{ language?: UiLanguage }>(getCookie(USER_SETTINGS_COOKIE))
  return legacy?.language ?? 'ru'
}

export function writeUiLanguage(language: UiLanguage): void {
  writeUiPrefs({ language })
}

export function readUiThemeMode(): UiThemeMode {
  const prefs = readUiPrefs()
  return prefs.themeMode ?? 'system'
}

export function writeUiThemeMode(themeMode: UiThemeMode): void {
  writeUiPrefs({ themeMode })
}
